import { useState } from 'react'
import {
  IonRow,
  IonCol,
  IonButton,
  useIonRouter,
  IonImg,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
} from '@ionic/react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { useTextAreaRef } from '../../../utils/forms'
import SimpleSelectPhoto from '../../../components/communication/SimpleSelectPhoto'
import TakePhoto from '../../../components/communication/TakePhoto'
import { Card, CardContent } from '../../../components/ui/card'
import {
  MergeFieldButtons,
  MergeFieldButton,
} from '../../../components/ui/form/merge-field-buttons'
import { TextArea } from '../../../components/ui/form/textarea'
import { useUpdateBroadcast } from '../mutations'
import { useBroadcastClientCounts } from '../queries'
import { useComposeShopWithLink } from '../utils'
import { BroadcastLink } from './broadcast-link'
import { BroadcastPhoto } from './broadcast-photo'
import { BroadcastShopWithSelect } from './broadcast-shopwith-select'

import type { UpdateBroadcastInputs } from '../broadcast.schema'

type Props = {
  broadcastId: string
  disabled: boolean
  htmlPreview: string | null
  unlayerDesign: any
}

function BroadcastEmailField({
  broadcastId,
  disabled,
  htmlPreview,
  unlayerDesign,
}: Props) {
  const emailMessageRef = useTextAreaRef()

  const router = useIonRouter()

  const clientCounts = useBroadcastClientCounts(broadcastId)
  const updateBroadcast = useUpdateBroadcast()

  const { control, getValues, setValue } =
    useFormContext<UpdateBroadcastInputs>()
  const attachments = useWatch({ control, name: 'setAttachmentsEmail' })
  const messageType = useWatch({ control, name: 'messageType' })
  const [collectionIds, lookIds, productIds] = useWatch({
    control,
    name: ['collectionIdsEmail', 'lookIdsEmail', 'productIdsEmail'],
  })

  const [currentEmailType, setCurrentEmailType] = useState<'html' | 'sms'>(
    !getValues('emailMessage') ? 'html' : !unlayerDesign ? 'sms' : 'html'
  )
  const shopWithLink = useComposeShopWithLink({
    productIds: productIds ?? [],
    lookIds: lookIds ?? [],
    collectionIds: collectionIds ?? [],
  })

  function resetBroadcastHtmlEmail() {
    if (unlayerDesign)
      updateBroadcast.mutate({
        objectId: broadcastId,
        broadcast: { emailMessage: '', unlayerDesign: null },
      })
  }

  return (
    <>
      {messageType === 'email' || messageType === 'prioSmsEmail' ? (
        <div className="py-2">
          {!disabled ? (
            <IonRow className="justify-center">
              <IonCol size="auto" className="justify-center">
                {currentEmailType === 'sms' ? (
                  <IonButton
                    size="small"
                    color="yellow"
                    onClick={() => {
                      setCurrentEmailType('html')
                      router.push(
                        `${
                          router.routeInfo.pathname
                        }/html-email?displayConditions=${
                          getValues('fromUserRule') === 'assignedTo'
                            ? 'show'
                            : 'hide'
                        }`
                      )
                    }}
                  >
                    Create HTML Email
                  </IonButton>
                ) : (
                  <div className="flex gap-x-3">
                    <IonButton
                      size="small"
                      color="yellow"
                      onClick={() => {
                        router.push(
                          `./${broadcastId}/html-email?displayConditions=${
                            getValues('fromUserRule') === 'assignedTo'
                              ? 'show'
                              : 'hide'
                          }`
                        )
                      }}
                    >
                      Edit HTML Email
                    </IonButton>
                    <IonButton
                      size="small"
                      color="secondary"
                      onClick={() => {
                        setCurrentEmailType('sms')
                        setValue('emailMessage', '')
                        resetBroadcastHtmlEmail()
                      }}
                    >
                      Write Plain Text Email
                    </IonButton>
                  </div>
                )}
              </IonCol>
            </IonRow>
          ) : null}

          <IonRow className="ion-padding-horizontal ion-justify-content-between ion-align-items-center">
            <IonCol size="auto">
              <h5 className="space-x-2">
                Email:
                {getValues('emailMessage')?.includes('{{#') ? (
                  <span className="font-semibold">
                    This email contains dynamic content.
                  </span>
                ) : null}
              </h5>
            </IonCol>
            <IonCol size="auto">
              <IonButton
                size="small"
                color="tertiary"
                fill="clear"
                className="pointer-events-none"
              >
                {clientCounts.data?.emailClientCount} Clients
              </IonButton>
            </IonCol>
          </IonRow>

          <div className="relative">
            <Card>
              <CardContent>
                <IonItem color="secondary" style={{ '--padding-start': 0 }}>
                  <IonLabel>Subject:</IonLabel>
                  <Controller
                    control={control}
                    name="emailSubject"
                    render={({ field: { value, onChange } }) => (
                      <IonInput
                        value={value}
                        onIonChange={onChange}
                        name="subject"
                        autocapitalize="on"
                        disabled={disabled}
                      />
                    )}
                  />
                </IonItem>
                <div>
                  <p className="text-lg">
                    {/* Not 100% sure of unlayer types - play it safe w/optional chains */}
                    Email Preheader:{' '}
                    {unlayerDesign?.body?.values?.preheaderText}
                  </p>
                </div>
                <div className="ion-padding-top">
                  {!disabled ? (
                    <div className="flex justify-end">
                      <IonButton
                        size="small"
                        fill="outline"
                        onClick={() => {
                          setValue('emailMessage', '', { shouldDirty: true })
                          resetBroadcastHtmlEmail()
                        }}
                      >
                        Clear
                      </IonButton>
                    </div>
                  ) : null}
                  {htmlPreview ? (
                    <div className="flex justify-center">
                      <IonImg alt="Email Preview" src={htmlPreview} />
                    </div>
                  ) : (
                    <>
                      <Controller
                        control={control}
                        name="emailMessage"
                        render={({ field: { value, onChange } }) => (
                          <TextArea
                            ref={emailMessageRef}
                            value={value}
                            onIonChange={onChange}
                            placeholder="Write your email here..."
                            autoGrow
                            autocapitalize="on"
                            disabled={disabled}
                          />
                        )}
                      />
                      {!disabled ? (
                        <IonRow className="ion-justify-content-between">
                          <IonCol size="auto" className="space-x-2">
                            <MergeFieldButtons
                              mergeRef={emailMessageRef}
                              onInsert={(value) =>
                                setValue('emailMessage', value)
                              }
                            >
                              <MergeFieldButton placeholder="<First Name>">
                                First Name
                              </MergeFieldButton>
                              <MergeFieldButton placeholder="<Last Name>">
                                Last Name
                              </MergeFieldButton>
                              <MergeFieldButton placeholder="<Lifetime Loyalty Points>">
                                Lifetime Loyalty Points
                              </MergeFieldButton>
                              <MergeFieldButton placeholder="<Active Loyalty Points>">
                                Active Loyalty Points
                              </MergeFieldButton>
                              <MergeFieldButton placeholder="<Active Loyalty Visits>">
                                Active Loyalty Visits
                              </MergeFieldButton>
                              <MergeFieldButton placeholder="<Active Loyalty Rewards>">
                                Active Loyalty Rewards
                              </MergeFieldButton>
                            </MergeFieldButtons>
                          </IonCol>
                        </IonRow>
                      ) : null}
                      {shopWithLink ? (
                        <BroadcastLink
                          shopWithLink={shopWithLink}
                          onDelete={() => {
                            setValue('collectionIdsEmail', null)
                            setValue('lookIdsEmail', null)
                            setValue('productIdsEmail', null)
                          }}
                        />
                      ) : null}
                      {attachments ? (
                        <BroadcastPhoto
                          photo={attachments[0]}
                          onDelete={() => setValue('setAttachmentsEmail', null)}
                        />
                      ) : null}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            {currentEmailType === 'html' && !unlayerDesign ? (
              <button
                className="absolute inset-0 z-10 flex items-center justify-center bg-black/60"
                onClick={() =>
                  router.push(
                    `./${broadcastId}/html-email?displayConditions=${
                      getValues().fromUserRule === 'assignedTo'
                        ? 'show'
                        : 'hide'
                    }`
                  )
                }
              >
                <span className="bg-ion-color-yellow rounded-lg p-3 text-lg text-black">
                  Design Email
                </span>
              </button>
            ) : null}
          </div>

          {!htmlPreview && !disabled ? (
            <div className="flex justify-end gap-x-2 px-4 py-2">
              <TakePhoto
                onTakePhoto={(e) => {
                  setValue('setAttachmentsEmail', [e])
                }}
              />
              <SimpleSelectPhoto
                onSelectPhoto={(e) => {
                  setValue('setAttachmentsEmail', [e])
                }}
              />
              <BroadcastShopWithSelect
                onConfirm={(type, value) => {
                  setValue(`${type}Email`, value)
                }}
              />
            </div>
          ) : null}
          <IonLoading
            isOpen={updateBroadcast.isLoading}
            message="Removing Email Design..."
          />
        </div>
      ) : null}
    </>
  )
}

export { BroadcastEmailField }
